import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

import type { CampaignDailyPoint, CampaignTableRow } from "../../services/use-dashboard"
import { formatBucketKey } from "../../lib/format-date"
import { formatCurrency, formatRoas } from "../../lib/money"
import { CHART_TOOLTIP_CONTENT_STYLE } from "../ui/chart-style"

// Daily ROAS above the campaign table (#577) — the question the table cannot
// answer, which is whether a bad number is one bad day or three bad weeks.
//
// ONE BAR PER DAY, COLOURED BY WHETHER IT PAID. Blue above break-even, red
// below, with a dashed break-even rule across the plot. That is the shape Sofia
// already reads her weekly PPC report in, and it puts the decision in the colour
// rather than in the reader's head. Blue/red rather than green/red on purpose:
// green against red is the classic red-green confusion, and this pair separates
// under every simulation (CVD ΔE 26.0 light / 22.9 dark against a target of 8).
// The legend names both states, because a status must never be colour alone.
//
// ONE PLOT, ONE MEASURE. A spend panel rode underneath until Sofia asked for it
// gone; the daily spend still reaches the reader through the tooltip, which is
// where she wanted it. If spend ever comes back as a mark it gets its OWN plot
// sharing this x-axis, never a second y-scale on this one: a ratio and a money
// amount on two arbitrary scales invent a correlation the data does not carry,
// which is the single worst chart mistake there is.
//
// THE CAP. Cross-sell days run at 20–30× while search sits near 1×, so an
// unclamped axis squashes the break-even rule onto the baseline and the whole
// chart says nothing. Bars are clamped at 4× break-even and a clamped bar
// carries its true figure as a direct label, so nothing is hidden — the reader
// sees both that it went off the top and exactly how far.
//
// THE BREAK-EVEN VALUE COMES FROM THE ROWS, spend-weighted over the whole window
// — one derivation shared with the table's Breakeven column, never a second one
// that could drift. It pools every programme because the bars do; it is not the
// average of whatever tab is open below.

/** Bars are clamped at this multiple of break-even. Four is Sofia's own report's
 *  choice and it works for the same reason: it keeps the rule roughly a quarter
 *  of the way up, where it can be read against both a 0.5× day and a 20× one. */
const CAP_MULTIPLE = 4

/** Blue cleared it, red did not, grey means there was no rule to clear. */
const STATE_FILL = {
  above: "var(--color-chart-1)",
  below: "var(--color-destructive)",
  unknown: "var(--color-muted-foreground)",
} as const

const ROAS_TOOLTIP_ITEM_STYLE = {
  color: "var(--color-popover-foreground)",
} as const

type ChartRow = {
  date: string
  label: string
  /** What the bar draws — clamped at the cap. */
  roas: number | null
  /** What the day actually was; equal to `roas` unless it was clamped. */
  trueRoas: number | null
  /** Set only on a clamped bar, so exactly those bars get a direct label. */
  clampedLabel: string | null
  /** `above` / `below` colour the bar; `unknown` is for a window with no rule to
   *  judge against, where painting every bar red would call a 25× day a loss. */
  state: "above" | "below" | "unknown"
  spend: number
}

/**
 * The ROAS this window's spend had to clear, weighted BY SPEND across the rows.
 *
 * Taken from the rows the table renders rather than recomputed, so it is the same
 * arithmetic as the Breakeven column beneath it rather than a second derivation.
 * Spend weighting is the honest one for a single rule over a mixed window: it
 * answers "what did the money actually need", so a €400 product counts for more
 * than a €4 one.
 *
 * It is weighted over the WHOLE window, every programme, because the chart pools
 * every programme too. So on a filtered tab the rule is deliberately not the
 * average of the visible rows — it is what the window's whole spend mix had to
 * clear, and the caption says the chart pools.
 *
 * Null when no row carries a break-even at all — every product's margin was
 * unknown, and a rule drawn from nothing would be an invention.
 */
export function windowBreakevenRoas(rows: CampaignTableRow[]): number | null {
  let weighted = 0
  let spend = 0
  for (const row of rows) {
    if (row.breakevenRoas === null || row.adSpend <= 0) {
      continue
    }
    weighted += row.breakevenRoas * row.adSpend
    spend += row.adSpend
  }
  return spend > 0 ? Math.round((weighted / spend) * 100) / 100 : null
}

/** Every day in the window, in order, whether or not it carries facts.
 *
 *  Built from the WINDOW rather than from the points, so a quiet stretch stays
 *  visible as a hole instead of being silently compressed away — the x-axis would
 *  otherwise lie about how long a slump lasted. */
export function toChartRows(
  points: CampaignDailyPoint[],
  window: { from: string; to: string },
  breakeven: number | null
): ChartRow[] {
  const byDate = new Map(points.map((point) => [point.date, point]))
  const cap = breakeven === null ? null : breakeven * CAP_MULTIPLE
  return eachDay(window).map((date) => {
    const point = byDate.get(date) ?? null
    const roas = point?.roas ?? null
    const clamped = roas !== null && cap !== null && roas > cap
    return {
      date,
      // `formatBucketKey` is the single owner of a client bucket label, and the
      // card's own header already prints its window through it.
      label: formatBucketKey(date, "day"),
      roas: clamped ? cap : roas,
      trueRoas: roas,
      clampedLabel: clamped && roas !== null ? formatRoas(roas) : null,
      state: barState(roas, breakeven),
      spend: point?.spend ?? 0,
    }
  })
}

/** With no rule there is nothing to judge against, so the bar stays neutral
 *  rather than taking the colour the legend calls "below break-even" — that would
 *  paint a 25× day as a loss. A day with no ROAS at all draws no bar, so its
 *  state is never seen. */
function barState(roas: number | null, breakeven: number | null): ChartRow["state"] {
  if (breakeven === null) {
    return "unknown"
  }
  return roas !== null && roas >= breakeven ? "above" : "below"
}

/** Inclusive ISO day range, walked in UTC so a local DST shift cannot drop or
 *  duplicate a day. Capped at a year — the read that feeds it is bounded too. */
function eachDay(window: { from: string; to: string }): string[] {
  const days: string[] = []
  const end = Date.parse(`${window.to}T00:00:00Z`)
  let cursor = Date.parse(`${window.from}T00:00:00Z`)
  if (Number.isNaN(cursor) || Number.isNaN(end)) {
    return days
  }
  while (cursor <= end && days.length < 366) {
    days.push(new Date(cursor).toISOString().slice(0, 10))
    cursor += 24 * 60 * 60 * 1000
  }
  return days
}

export function CampaignRoasChart({
  points,
  rows,
  window,
  currency,
}: {
  points: CampaignDailyPoint[]
  rows: CampaignTableRow[]
  window: { from: string; to: string } | undefined
  currency: string
}) {
  if (!window || points.length === 0) {
    return null
  }
  const breakeven = windowBreakevenRoas(rows)
  const chartRows = toChartRows(points, window, breakeven)

  return (
    <section className="border-b border-border/70 px-5 pb-4 pt-4" aria-labelledby="roas-per-day">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
        <span id="roas-per-day" className="text-xs font-medium text-foreground">
          ROAS per day
        </span>

        {/* The state is never carried by colour alone — and with no rule there is
            no above/below to name, so the legend says that instead of promising
            two states the bars cannot be in. */}
        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          {breakeven === null ? (
            <span className="flex items-center gap-1.5">
              <Swatch fill={STATE_FILL.unknown} />
              no break-even to judge against
            </span>
          ) : (
            <>
              <span className="flex items-center gap-1.5">
                <Swatch fill={STATE_FILL.above} />
                above break-even
              </span>

              <span className="flex items-center gap-1.5">
                <Swatch fill={STATE_FILL.below} />
                below break-even
              </span>

              <span>dashed rule = break-even {formatRoas(breakeven)}</span>
            </>
          )}
        </div>
      </div>

      <p className="mt-1 text-xs text-muted-foreground">
        Both programmes together, whichever one the table below is showing. A flat red sliver is a
        day that spent and earned nothing; a missing bar is a day with no ROAS to state at all —
        nothing was spent, or the revenue could not be read.
        {breakeven === null
          ? " No product in this window carries a margin, so there is no break-even to colour against."
          : ` A bar clamped at ${CAP_MULTIPLE}× break-even is labelled with its real figure.`}
      </p>

      <RoasPanel rows={chartRows} breakeven={breakeven} currency={currency} />
    </section>
  )
}

function Swatch({ fill }: { fill: string }) {
  return <span aria-hidden="true" className="size-2 rounded-full" style={{ background: fill }} />
}

/** The only plot: one bar per day against the break-even rule.
 *
 *  The container is tall enough to hold the x-axis band as well as the bars — a
 *  fixed height that fits only the plot gives the card a tiny nested scrollbar. */
function RoasPanel({
  rows,
  breakeven,
  currency,
}: {
  rows: ChartRow[]
  breakeven: number | null
  currency: string
}) {
  return (
    <div className="mt-2 h-[260px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} margin={{ top: 14, right: 12, left: 0, bottom: 0 }}>
          {/* Solid hairlines: a dashed grid reads as a threshold that isn't there,
              and this plot already has one real dashed threshold on it. */}
          <CartesianGrid stroke="var(--color-border)" strokeOpacity={0.6} vertical={false} />

          <XAxis
            dataKey="label"
            tick={{ fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            interval="preserveStartEnd"
            minTickGap={16}
          />

          <YAxis
            tick={{ fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            width={48}
            tickFormatter={(value: number) => formatRoas(value)}
          />

          <Tooltip
            contentStyle={CHART_TOOLTIP_CONTENT_STYLE}
            itemStyle={ROAS_TOOLTIP_ITEM_STYLE}
            labelFormatter={dayLabel}
            // The TRUE ROAS, never the clamped one — a tooltip that repeated the
            // cap would hide exactly the days the cap exists for. The day's spend
            // rides along because that is the first thing you want to know about a
            // red bar, and it is no longer drawn anywhere.
            formatter={(_value, _name, item) => [
              `${roasText(item)} · ${formatCurrency(spendOf(item), currency)} spent`,
              "ROAS",
            ]}
          />

          {breakeven === null ? null : (
            <ReferenceLine
              y={breakeven}
              // Without this the rule is DISCARDED whenever it sits above the
              // tallest bar — which is exactly a window that lost money on every
              // single day, the one that most needs to show what it fell short
              // of. The legend promises the rule; the plot has to keep it.
              ifOverflow="extendDomain"
              stroke="var(--color-muted-foreground)"
              strokeDasharray="4 4"
              label={{
                value: `break-even ${formatRoas(breakeven)}`,
                position: "insideTopLeft",
                fontSize: 11,
                fill: "var(--color-muted-foreground)",
              }}
            />
          )}

          <Bar
            dataKey="roas"
            radius={[4, 4, 0, 0]}
            // A day that SPENT and earned nothing is a ROAS of 0, and recharts
            // draws a zero-height rect as nothing at all — so the worst day in
            // the window looked identical to a day nobody advertised on. The
            // floor gives a real zero a visible sliver. A null still draws
            // nothing, which is correct: that day has no ROAS to show.
            minPointSize={2}
            isAnimationActive={false}
          >
            {rows.map((row) => (
              <Cell key={row.date} fill={STATE_FILL[row.state]} />
            ))}

            {/* Selective by construction: only a clamped bar carries a label. */}
            <LabelList
              dataKey="clampedLabel"
              position="top"
              fontSize={10}
              fill="var(--color-muted-foreground)"
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

/** The tooltip heads with the FULL ISO day; the axis tick is abbreviated to fit,
 *  and a reader hovering a slump needs to know which day it was. */
function dayLabel(_label: unknown, payload: readonly { payload?: { date?: string } }[]): string {
  return payload[0]?.payload?.date ?? ""
}

function payloadOf(item: unknown): { trueRoas?: number | null; spend?: number } {
  return (
    (item as { payload?: { trueRoas?: number | null; spend?: number } } | undefined)?.payload ?? {}
  )
}

function roasText(item: unknown): string {
  const roas = payloadOf(item).trueRoas
  return roas === null || roas === undefined ? "—" : formatRoas(roas)
}

function spendOf(item: unknown): number {
  return payloadOf(item).spend ?? 0
}
